import { toast } from "sonner";
import { Car, FileText, XCircle, Bell, ChevronRight, X, UserPlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { AdminNotificationType } from "@/lib/services/admin-notification.service";

/**
 * Configuration for each notification type
 */
const notificationConfig: Record<
  AdminNotificationType,
  {
    icon: typeof Car;
    title: string;
    bgColor: string;
    iconColor: string;
    borderColor: string;
    actionPath: string;
    actionLabel: string;
  }
> = {
  admin_vehicle_pending: {
    icon: Car,
    title: "Nouveau véhicule",
    bgColor: "bg-blue-50 dark:bg-blue-950/50",
    iconColor: "text-blue-600 dark:text-blue-400",
    borderColor: "border-l-blue-500",
    actionPath: "/admin/vehicles",
    actionLabel: "Voir les véhicules",
  },
  admin_document_pending: {
    icon: FileText,
    title: "Nouveau document",
    bgColor: "bg-amber-50 dark:bg-amber-950/50",
    iconColor: "text-amber-600 dark:text-amber-400",
    borderColor: "border-l-amber-500",
    actionPath: "/admin/documents",
    actionLabel: "Voir les documents",
  },
  admin_cancellation_pending: {
    icon: XCircle,
    title: "Nouvelle annulation",
    bgColor: "bg-red-50 dark:bg-red-950/50",
    iconColor: "text-red-600 dark:text-red-400",
    borderColor: "border-l-red-500",
    actionPath: "/admin/cancellations",
    actionLabel: "Voir les annulations",
  },
  admin_renter_signup: {
    icon: UserPlus,
    title: "Nouvelle inscription",
    bgColor: "bg-emerald-50 dark:bg-emerald-950/50",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    borderColor: "border-l-emerald-500",
    actionPath: "/admin/users",
    actionLabel: "Voir les utilisateurs",
  },
};

/**
 * Props for the admin notification toast
 */
interface AdminNotificationToastProps {
  type: AdminNotificationType;
  message: string;
  onAction?: () => void;
  onDismiss?: () => void;
}

/**
 * Custom toast content component
 */
function AdminNotificationToastContent({
  type,
  message,
  onAction,
  onDismiss,
}: AdminNotificationToastProps) {
  const config = notificationConfig[type];
  const Icon = config?.icon || Bell;

  return (
    <div
      className={cn(
        "relative flex items-start gap-3 p-4 rounded-lg border-l-4 shadow-lg",
        "bg-white dark:bg-gray-900",
        "min-w-[320px] max-w-[420px]",
        config?.borderColor || "border-l-gray-500"
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          "flex-shrink-0 p-2 rounded-full",
          config?.bgColor || "bg-gray-100"
        )}
      >
        <Icon className={cn("h-5 w-5", config?.iconColor || "text-gray-600")} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pr-6">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {config?.title || "Notification"}
        </p>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
          {message}
        </p>

        {/* Action button */}
        {onAction && (
          <button
            onClick={onAction}
            className={cn(
              "mt-2 inline-flex items-center gap-1.5 text-sm font-medium",
              "text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300",
              "transition-colors duration-200"
            )}
          >
            {config?.actionLabel || "Voir"}
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Close button */}
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="Fermer"
        >
          <X className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
        </button>
      )}
    </div>
  );
}

/**
 * Show an admin notification toast
 * Uses sonner's custom toast with rich content
 */
export function showAdminNotificationToast(
  type: AdminNotificationType,
  message: string,
  options?: {
    duration?: number;
    onAction?: () => void;
  }
) {
  const config = notificationConfig[type];

  // Play notification sound (optional - browser API)
  try {
    // Only play sound if user has interacted with the page
    if (document.hasFocus()) {
      const audio = new Audio("/sounds/notification.mp3");
      audio.volume = 0.3;
      audio.play().catch(() => {
        // Silently fail if audio can't play (autoplay restrictions)
      });
    }
  } catch {
    // Audio not supported or file not found
  }

  const toastId = toast.custom(
    (t) => (
      <AdminNotificationToastContent
        type={type}
        message={message}
        onAction={() => {
          if (options?.onAction) {
            options.onAction();
          } else if (config?.actionPath) {
            window.location.href = config.actionPath;
          }
          toast.dismiss(t);
        }}
        onDismiss={() => toast.dismiss(t)}
      />
    ),
    {
      duration: options?.duration || 8000, // 8 seconds by default
      position: "top-right",
      className: "admin-notification-toast",
    }
  );

  return toastId;
}

/**
 * Show multiple notifications as a summary
 */
export function showAdminNotificationSummary(
  counts: { vehicles: number; documents: number; cancellations: number },
  onAction?: () => void
) {
  const total = counts.vehicles + counts.documents + counts.cancellations;
  if (total === 0) return;

  const parts: string[] = [];
  if (counts.vehicles > 0) {
    parts.push(`${counts.vehicles} véhicule${counts.vehicles > 1 ? "s" : ""}`);
  }
  if (counts.documents > 0) {
    parts.push(`${counts.documents} document${counts.documents > 1 ? "s" : ""}`);
  }
  if (counts.cancellations > 0) {
    parts.push(`${counts.cancellations} annulation${counts.cancellations > 1 ? "s" : ""}`);
  }

  const message = `${parts.join(", ")} en attente de traitement`;

  toast.custom(
    (t) => (
      <div
        className={cn(
          "relative flex items-start gap-3 p-4 rounded-lg border-l-4 shadow-lg",
          "bg-white dark:bg-gray-900 border-l-green-500",
          "min-w-[320px] max-w-[420px]"
        )}
      >
        <div className="flex-shrink-0 p-2 rounded-full bg-green-50 dark:bg-green-950/50">
          <Bell className="h-5 w-5 text-green-600 dark:text-green-400" />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {total} notification{total > 1 ? "s" : ""} en attente
          </p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {message}
          </p>
          <button
            onClick={() => {
              if (onAction) {
                onAction();
              }
              toast.dismiss(t);
            }}
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-green-600 hover:text-green-700 dark:text-green-400"
          >
            Voir les notifications
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <button
          onClick={() => toast.dismiss(t)}
          className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <X className="h-4 w-4 text-gray-400" />
        </button>
      </div>
    ),
    {
      duration: 10000,
      position: "top-right",
    }
  );
}

export default AdminNotificationToastContent;

